/**
 * Saying out loud what became of the owner's "payment received" mail.
 *
 * The mark-paid surfaces all show their own confirmation ("Rent recorded as
 * paid") the moment the ledger write commits, and that toast was the whole
 * story an admin ever got. The mail that should follow it was fired and
 * forgotten — see the header on owner-payment-notify.ts — so when it did not
 * go out, nothing said so. An owner went a month with no rent receipt while
 * every screen involved looked like it had worked.
 *
 * This is the second half of that sentence. It resolves the notification and
 * raises exactly one follow-up toast:
 *
 *   sent / already_sent  → a quiet success naming the owner, so "was Pascal
 *                          told?" is answered on screen instead of by asking
 *                          him.
 *   not_sent             → a warning carrying the reason, because each reason
 *                          has a different fix (add an address; leave a muted
 *                          owner alone; retry a rejected send).
 *   unknown              → a warning that says plainly that we do not know,
 *                          rather than implying either outcome.
 *   not_applicable       → nothing. No mail was ever due.
 *
 * Deliberately a follow-up toast rather than text folded into the caller's own
 * message: the ledger write must not wait on an edge function round-trip
 * before confirming the thing that already succeeded, and eight call sites
 * with eight different primary messages would each need their own composed
 * string and translation key.
 *
 * Never throws. A notification problem is not a payment problem, and the
 * payment is already recorded by the time this runs.
 */
import { toast } from "sonner";
import {
  notifyOwnerPaymentReceived,
  type OwnerNotifyOutcome,
  type SettledPayment,
} from "./owner-payment-notify";

/** "the owner" when the mail resolved to nobody nameable. */
function who(owner: string | null): string {
  return owner?.trim() || "the owner";
}

export function describeOwnerNotifyOutcome(
  outcome: OwnerNotifyOutcome
): { kind: "success" | "warning"; message: string } | null {
  switch (outcome.status) {
    case "sent":
      return {
        kind: "success",
        message: `${who(outcome.owner)} was emailed about this payment.`,
      };
    case "already_sent":
      return {
        kind: "success",
        message: `${who(outcome.owner)} was already emailed about this payment.`,
      };
    case "not_sent":
      return {
        kind: "warning",
        message: `${who(outcome.owner)} was NOT emailed — ${outcome.detail}.`,
      };
    case "unknown":
      return {
        kind: "warning",
        message:
          "The payment is recorded, but we could not confirm the owner was emailed. " +
          "Check the owner's email history before telling them.",
      };
    case "not_applicable":
      return null;
  }
}

/**
 * Fire-and-forget from the caller's side — `void` it — but not answer-and-
 * discard: the outcome reaches the screen.
 */
export async function reportOwnerPaymentNotification(
  payment: SettledPayment | null | undefined
): Promise<void> {
  let described: { kind: "success" | "warning"; message: string } | null = null;
  try {
    described = describeOwnerNotifyOutcome(
      await notifyOwnerPaymentReceived(payment)
    );
  } catch {
    // notifyOwnerPaymentReceived already resolves its own failures to an
    // "unknown" outcome, so reaching here means something unforeseen. Warn
    // rather than say nothing — silence is the bug this file exists to fix.
    described = {
      kind: "warning",
      message:
        "The payment is recorded, but the owner notification could not be checked.",
    };
  }
  if (!described) return;
  if (described.kind === "success") {
    toast.success(described.message);
  } else {
    // Held open: a missed owner notification is a task for whoever is at the
    // screen, and the default dismiss would take it away before they read it.
    toast.warning(described.message, { duration: 10000 });
  }
}
